/**
 * Interactive Reading editor — EAA-1755.
 *
 * Authoring form for the "interactive-reading" content type. Layout follows the
 * approved Figma: Title, Instructions, a "Text content" section with an
 * Enter-text / Upload-file toggle and character counter, an inline model-reading
 * preview, and the Auto-Grading toggle. The TTS synthesis (audio + word timing)
 * is produced by the aihub AI job via `speechTimingService` — never a direct
 * fetch inside this component (per content-type API convention).
 */

import * as React from 'react';
import {
    Input,
    Label,
    Textarea,
    Button,
    Checkbox,
    cn,
    AudioPlayer,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@ai-content-hub/content-kit';
import { useLocale } from '@ai-content-hub/content-core';
import {
    speechTimingService,
    type SpeechTimingVoicesResult,
} from '@ai-content-hub/shared-api';
import {
    Type,
    Upload,
    AudioLines,
    Loader2,
    RefreshCw,
    AlertCircle,
    FileText,
    X,
    Mic,
    HelpCircle,
} from 'lucide-react';
import type { InteractiveReadingConfig, InteractiveReadingData } from './schema';
import {
    MAX_READING_TEXT_LENGTH,
    defaultInteractiveReadingData,
    defaultInteractiveReadingI18n,
} from './schema';

export interface InteractiveReadingEditProps {
    value: InteractiveReadingData;
    onChange: (value: InteractiveReadingData) => void;
    config?: InteractiveReadingConfig;
    onConfigChange?: (config: InteractiveReadingConfig) => void;
    disabled?: boolean;
    errors?: Record<string, string>;
    /**
     * Tenant id forwarded by the host editor (ContentEditor → EditComponent, e.g.
     * from the embed URL / INIT postMessage). Needed so staff/superadmin JWTs —
     * which carry no tenant — resolve tenant context for the speech-timing AI job.
     */
    tenantId?: string;
    className?: string;
}

type InputMode = 'text' | 'file';

/** Fallback voice catalog if the API is unreachable (mirrors the agent default). */
const FALLBACK_VOICES: SpeechTimingVoicesResult = {
    voices: ['Aoede', 'Kore', 'Leda', 'Charon', 'Fenrir', 'Puck'],
    count: 6,
    default_index: 1,
    default_name: 'Kore',
};

export const InteractiveReadingEdit: React.FC<InteractiveReadingEditProps> = ({
    value,
    onChange,
    config,
    disabled = false,
    errors,
    tenantId,
    className,
}) => {
    // EAA-1755: merge the content-type default i18n under the stored config so
    // newly added keys (e.g. voiceLabel) resolve even for content saved before
    // the key existed; author-customised strings in config still win.
    const i18nConfig = React.useMemo(() => {
        const stored = config?.i18n;
        return {
            defaultLanguage: stored?.defaultLanguage ?? 'en',
            languages: {
                en: { ...defaultInteractiveReadingI18n.en, ...(stored?.languages?.en ?? {}) },
                vi: { ...defaultInteractiveReadingI18n.vi, ...(stored?.languages?.vi ?? {}) },
            },
        };
    }, [config?.i18n]);
    const { t } = useLocale({ config: i18nConfig });

    const data: InteractiveReadingData = { ...defaultInteractiveReadingData, ...value };

    const [inputMode, setInputMode] = React.useState<InputMode>('text');
    const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
    const [generating, setGenerating] = React.useState(false);
    const [generateError, setGenerateError] = React.useState<string | null>(null);
    const [voices, setVoices] = React.useState<SpeechTimingVoicesResult | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // EAA-1754: fetch the voice catalog to bind the dropdown; fall back to the
    // known default set if the API is unreachable.
    React.useEffect(() => {
        let cancelled = false;
        speechTimingService
            .listVoices({ tenantId })
            .then((res) => {
                if (!cancelled) setVoices(res);
            })
            .catch(() => {
                if (!cancelled) setVoices(FALLBACK_VOICES);
            });
        return () => {
            cancelled = true;
        };
    }, [tenantId]);

    const voiceCatalog = voices ?? FALLBACK_VOICES;
    const selectedVoice = data.voice ?? voiceCatalog.default_index;

    const update = (updates: Partial<InteractiveReadingData>) => {
        onChange({ ...data, ...updates });
    };

    const charCount = data.text.length;
    const atLimit = charCount >= MAX_READING_TEXT_LENGTH;
    // EAA-1755: audio is stale when the script no longer matches what it was built from.
    const stimulusStale =
        Boolean(data.stimulus) && data.stimulus?.generatedFrom !== data.text.trim();
    const canGenerate =
        !disabled &&
        !generating &&
        (inputMode === 'text' ? data.text.trim().length > 0 : Boolean(selectedFile));

    const handleTextChange = (text: string) => {
        // Clear stale audio implicitly by keeping generatedFrom check; keep stimulus
        // so the author still sees the last preview until they regenerate.
        update({ text: text.slice(0, MAX_READING_TEXT_LENGTH) });
    };

    const handleFilePick = (file: File | null) => {
        setGenerateError(null);
        setSelectedFile(file);
    };

    const handleGenerate = async () => {
        setGenerateError(null);
        setGenerating(true);
        try {
            // This content type is English-only (TTS voice); always request `en`.
            // Pass the host tenantId so staff/superadmin (no tenant on JWT) resolve
            // tenant context for the aihub job.
            const result = await speechTimingService.generate({
                text: inputMode === 'text' ? data.text : undefined,
                file: inputMode === 'file' && selectedFile ? selectedFile : undefined,
                language: 'en',
                voice: selectedVoice,
                tenantId,
            });
            const nextText = (result.text || data.text).slice(0, MAX_READING_TEXT_LENGTH);
            onChange({
                ...data,
                text: nextText,
                language: 'en',
                // Persist the voice we generated with (the user's selection), so the
                // dropdown stays on it instead of snapping to whatever the API echoes.
                voice: selectedVoice,
                stimulus: {
                    audioUrl: result.audio_url,
                    audioFormat: result.audio_format,
                    mimeType: result.mime_type,
                    transcript: result.transcript,
                    wordTimestamps: result.word_timestamps ?? [],
                    language: result.language,
                    generatedFrom: nextText.trim(),
                },
            });
            // After a file upload, surface the extracted text so the author can edit it.
            if (inputMode === 'file') {
                setInputMode('text');
                setSelectedFile(null);
            }
        } catch {
            setGenerateError(t('generateError'));
        } finally {
            setGenerating(false);
        }
    };

    const wordCount = data.stimulus?.wordTimestamps?.length ?? 0;

    return (
        <div
            className={cn(
                'interactive-reading-edit flex h-full min-h-0 flex-col gap-6 overflow-y-auto p-1',
                className
            )}
        >
            {/* Title */}
            <div className="interactive-reading-edit-field space-y-2">
                <Label htmlFor="ir-title">
                    {t('title')} <span className="text-destructive">*</span>
                </Label>
                <Input
                    id="ir-title"
                    value={data.title ?? ''}
                    onChange={(e) => update({ title: e.target.value })}
                    placeholder={t('enterTitle')}
                    disabled={disabled}
                    className={cn(errors?.title && 'border-destructive')}
                />
                {errors?.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>

            {/* Instructions */}
            <div className="interactive-reading-edit-field space-y-2">
                <Label htmlFor="ir-instructions">{t('instructionsOptional')}</Label>
                <Textarea
                    id="ir-instructions"
                    value={data.description ?? ''}
                    onChange={(e) => update({ description: e.target.value })}
                    placeholder={t('enterInstructions')}
                    disabled={disabled}
                    rows={2}
                />
            </div>

            {/* Text content (Text to Speech) */}
            <div className="interactive-reading-edit-field space-y-3">
                <div className="flex items-center gap-1.5">
                    <Label htmlFor="ir-text">
                        {t('textLabel')} <span className="text-destructive">*</span>
                    </Label>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    aria-label={t('textHelper')}
                                    className="interactive-reading-edit-help text-muted-foreground transition-colors duration-200 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                >
                                    <HelpCircle className="h-4 w-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs">
                                {t('textHelper')}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>

                {/* Input-mode segmented toggle */}
                <div className="interactive-reading-edit-modes inline-flex rounded-md border border-border bg-muted/30 p-1">
                    <button
                        type="button"
                        onClick={() => setInputMode('text')}
                        disabled={disabled}
                        className={cn(
                            'flex items-center gap-2 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors duration-200',
                            inputMode === 'text'
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Type className="h-4 w-4" />
                        {t('inputModeText')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setInputMode('file')}
                        disabled={disabled}
                        className={cn(
                            'flex items-center gap-2 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors duration-200',
                            inputMode === 'file'
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Upload className="h-4 w-4" />
                        {t('inputModeFile')}
                    </button>
                </div>

                {inputMode === 'text' ? (
                    <div className="space-y-1.5">
                        <Textarea
                            id="ir-text"
                            value={data.text}
                            onChange={(e) => handleTextChange(e.target.value)}
                            placeholder={t('textPlaceholder')}
                            disabled={disabled}
                            rows={7}
                            maxLength={MAX_READING_TEXT_LENGTH}
                            className={cn(
                                'interactive-reading-edit-textarea resize-y',
                                errors?.text && 'border-destructive'
                            )}
                        />
                        <div className="flex items-center justify-between">
                            {errors?.text ? (
                                <p className="text-sm text-destructive">{errors.text}</p>
                            ) : (
                                <span />
                            )}
                            <span
                                className={cn(
                                    'text-xs tabular-nums',
                                    atLimit ? 'text-destructive' : 'text-muted-foreground'
                                )}
                            >
                                {charCount}/{MAX_READING_TEXT_LENGTH} {t('charCount')}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="interactive-reading-edit-upload rounded-sm border border-dashed border-border/60 bg-muted/30 p-4">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".txt,.docx"
                            className="hidden"
                            onChange={(e) => handleFilePick(e.target.files?.[0] ?? null)}
                        />
                        {selectedFile ? (
                            <div className="flex items-center justify-between gap-3">
                                <span className="flex min-w-0 items-center gap-2 text-sm text-foreground">
                                    <FileText className="h-4 w-4 shrink-0 text-primary" />
                                    <span className="truncate">{selectedFile.name}</span>
                                </span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleFilePick(null)}
                                    disabled={disabled}
                                >
                                    <X className="h-4 w-4" />
                                    {t('removeFile')}
                                </Button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-2 py-2 text-center">
                                <Upload className="h-5 w-5 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground">{t('uploadHint')}</p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={disabled}
                                >
                                    {t('inputModeFile')}
                                </Button>
                            </div>
                        )}
                    </div>
                )}


                {/* Generate + model-reading preview */}
                <div className="interactive-reading-edit-generate flex flex-wrap items-center gap-3 pt-1">
                    <Button
                        type="button"
                        onClick={handleGenerate}
                        disabled={!canGenerate}
                        className="interactive-reading-edit-generate-btn"
                    >
                        {generating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : data.stimulus ? (
                            <RefreshCw className="h-4 w-4" />
                        ) : (
                            <AudioLines className="h-4 w-4" />
                        )}
                        {generating
                            ? t('generating')
                            : data.stimulus
                              ? t('regenerateAudio')
                              : t('generateAudio')}
                    </Button>

                    {/* EAA-1754: voice picker next to the generate action.
                        The 'voice' label sits inside the trigger as a prefix (antd-style). */}
                    <div className="interactive-reading-edit-voice">
                        <Select
                            value={String(selectedVoice)}
                            onValueChange={(v) => update({ voice: Number(v) })}
                            disabled={disabled || generating}
                        >
                            <SelectTrigger className="h-8 w-40" aria-label={t('voiceLabel')}>
                                <span className="flex min-w-0 items-center gap-1.5">
                                    <span className="text-muted-foreground shrink-0">
                                        {t('voiceLabel')}
                                    </span>
                                    <SelectValue placeholder={t('voiceLoading')} />
                                </span>
                            </SelectTrigger>
                            <SelectContent>
                                {voiceCatalog.voices.map((name, i) => (
                                    <SelectItem key={i} value={String(i)}>
                                        {name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {data.stimulus && !stimulusStale && !generating && (
                        <span className="text-sm text-success">
                            {t('audioReady')} · {wordCount} {t('wordsTimed')}
                        </span>
                    )}
                </div>

                {generateError && (
                    <p className="flex items-center gap-2 text-sm text-destructive">
                        <AlertCircle className="h-4 w-4" />
                        {generateError}
                    </p>
                )}
                {stimulusStale && !generating && (
                    <p className="flex items-center gap-2 text-sm text-warning">
                        <AlertCircle className="h-4 w-4" />
                        {t('stimulusStale')}
                    </p>
                )}

                {data.stimulus?.audioUrl && (
                    <div className="interactive-reading-edit-preview space-y-1.5 rounded-sm bg-muted/30 p-3">
                        <p className="text-xs font-medium text-muted-foreground">{t('preview')}</p>
                        <AudioPlayer
                            src={data.stimulus.audioUrl}
                            className="interactive-reading-edit-preview-audio"
                        />
                    </div>
                )}
            </div>

            {/* Auto-Grading */}
            <div className="interactive-reading-edit-autograde rounded-sm border border-border bg-muted/30 p-3">
                <div className="flex items-start gap-3">
                    <Checkbox
                        id="ir-autograde"
                        checked={data.autoGrade}
                        onCheckedChange={(checked) => update({ autoGrade: checked === true })}
                        disabled={disabled}
                        className="mt-0.5"
                    />
                    <div className="space-y-1">
                        <Label
                            htmlFor="ir-autograde"
                            className="flex cursor-pointer items-center gap-2"
                        >
                            <Mic className="h-4 w-4 text-primary" />
                            {t('autoGradeLabel')}
                        </Label>
                        <p className="text-sm text-muted-foreground">{t('autoGradeHelper')}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InteractiveReadingEdit;
